import { FC, useCallback } from "react";
import { FlatList, StyleProp, StyleSheet, Text, TouchableOpacity, View, ViewStyle } from "react-native";
import { useSelector } from "react-redux";
import { useNavigation } from "@react-navigation/native";

import { useIsMobileAndTablet } from "src/hooks/ui";
import { useAppDispatch } from "src/redux";
import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";
import { openCheckoutAll } from "src/redux/user/actions";
import { formatMoney } from "src/util/gFormatting";
import { TermsApplicationDetails } from "./connected";
import { useConfirmModal } from "src/hooks/modals";
import { PdText } from "./layout";
import { PdDivider } from "./layout/table";
import { PdButton, PdButtonLabel } from "./forms/button";
import { kRoute } from "src/kRoute";
import PdHandGiftBox from "./Icons/PdHandGiftBox";

const NotCheckoutableBox: FC<{ myCartNumber: number }> = ({ myCartNumber }) => {
  if (myCartNumber === 0) {
    return null;
  }

  return (
    <View style={styles.orderSummaryAlertContainer}>
      <Text style={styles.orderSummaryAlertHeaderText}>
        {myCartNumber} BRAND{myCartNumber > 1 && "S"} UNDER ORDER MINIMUM
      </Text>
      <Text numberOfLines={2} style={styles.orderSummaryAlertBodyText}>
        Brands in your cart that are under the order minimum will not be checked out.
      </Text>
    </View>
  );
};

const NotConnectedBox: FC<{ myCartNumber: number }> = ({ myCartNumber }) => {
  if (myCartNumber === 0) {
    return null;
  }

  return (
    <View style={styles.orderSummaryAlertContainer}>
      <Text style={styles.orderSummaryAlertHeaderText}>
        YOU ARE NOT CONNECTED TO {myCartNumber} BRAND{myCartNumber > 1 && "S"}
      </Text>
      <Text numberOfLines={2} style={styles.orderSummaryAlertBodyText}>
        Brands in your cart that you are not connected with will not be checked out.
      </Text>
    </View>
  );
};

const RewardsCreditDetails: FC<{ style?: StyleProp<ViewStyle> }> = ({ style }) => {
  const navigation = useNavigation();
  const rIsCompanyFetching = useSelector((state) => state.CompanyReducer.type === "none");
  const rCompany = useSelector((state) => state.CompanyReducer);

  const handleOnPressReferBrands = () => {
    navigation.navigate(kRoute.P19);
  };

  if (rIsCompanyFetching || rCompany.type !== "retailer") {
    return null;
  }

  const availableCreditAmount =
    (rCompany.availableRewardsCreditAmount ?? 0) + (rCompany.availableMarketplaceCreditAmount ?? 0);

  return (
    <View style={style}>
      <View style={[styles.flexRow, styles.alignCenter]}>
        <PdHandGiftBox style={styles.sizeSm} />
        <PdText style={[styles.textUpperCase, kFonts.bodySmallCenter, styles.ml2]}>Available Cash Back Rewards!</PdText>
      </View>
      <PdDivider style={styles.my1} />
      {availableCreditAmount ? (
        <PdText variant="subheader">{formatMoney(availableCreditAmount)}</PdText>
      ) : (
        <PdButton colorScheme="green" variant="outline" onPress={handleOnPressReferBrands}>
          <PdButtonLabel>Refer Brands</PdButtonLabel>
        </PdButton>
      )}
    </View>
  );
};

const OrderSummary: FC<{ isDraftPage?: boolean; onDraftSubmit?: () => void }> = ({
  isDraftPage = false,
  onDraftSubmit,
}) => {
  const isMobileTablet = useIsMobileAndTablet();
  const rCart = useSelector(({ UserReducer }) => UserReducer.cart);
  const aCheckoutableCarts = rCart?.filter((myCart) => (myCart.overMin && myCart.isConnected) || isDraftPage);
  const totalNumBrands = aCheckoutableCarts?.length ?? 0;
  const totalPriceWithShipping = aCheckoutableCarts?.reduce((x, y) => x + (y.totalPrice + y.totalPriceWithShipping), 0);
  const dispatch = useAppDispatch();
  const confirmModal = useConfirmModal();

  const _orderItemKeyExtractor = useCallback((myCartItem) => myCartItem.name, []);
  const _orderItemRenderFunction = useCallback(
    ({ item }) => (
      <View style={[styles.orderSummaryContainer, styles.my2]}>
        <Text style={styles.orderSummaryText}>{item.name}</Text>
        <Text style={styles.orderSummaryText}>{formatMoney(item.totalPrice + item.totalPriceWithShipping)}</Text>
      </View>
    ),
    [],
  );

  const _openAllCheckouts = async () => {
    const response = await dispatch(openCheckoutAll());

    if (response && response.cartsFinished.length > 0 && response.cartsNotFinished.length === 0) {
      confirmModal.openAsync({
        title: "Success!",
        description: "Your order has been submitted. You can view your order and its status in your Order page.",
        confirmButtonLabel: "OKAY",
        hideCancelButton: true,
      });
    }
  };

  return (
    <View style={isMobileTablet ? styles.widthFull : styles.widthMd}>
      <View style={styles.my2}>
        <FlatList
          keyExtractor={_orderItemKeyExtractor}
          data={aCheckoutableCarts}
          renderItem={_orderItemRenderFunction}
          scrollEnabled={false}
        />
        <View style={[styles.orderSummaryContainer, styles.my2]}>
          <Text style={styles.totalLabel}>Total</Text>
          <Text style={styles.totalAmount}>{formatMoney(totalPriceWithShipping ?? 0)}</Text>
        </View>
      </View>
      {isDraftPage ? (
        <TouchableOpacity style={styles.topBannerContainer} onPress={onDraftSubmit}>
          <Text style={styles.orderSummaryTotalCheckout}>Save & Share this Order</Text>
        </TouchableOpacity>
      ) : (
        <>
          <TouchableOpacity style={styles.topBannerContainer} onPress={_openAllCheckouts}>
            <Text style={styles.orderSummaryTotalCheckout}>
              PROCEED TO CHECK OUT ({totalNumBrands.toString()} BRAND{totalNumBrands > 1 ? "S" : ""})
            </Text>
          </TouchableOpacity>
          {!isDraftPage && (
            <>
              <TermsApplicationDetails style={styles.my4} />
              <RewardsCreditDetails style={styles.my4} />
              <View style={styles.divider} />
            </>
          )}
          <NotCheckoutableBox
            myCartNumber={rCart?.filter((myCart) => myCart.isConnected && !myCart.overMin)?.length ?? 0}
          />
          <NotConnectedBox myCartNumber={rCart?.filter((myCart) => !myCart.isConnected)?.length ?? 0} />
        </>
      )}
    </View>
  );
};

export default OrderSummary;

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  divider: {
    backgroundColor: kColors.gray300,
    height: 1,
    width: "100%",
  },
  flexRow: {
    flexDirection: "row",
  },
  ml2: {
    marginLeft: kStyleConst.kSpacing * 2,
  },
  my1: {
    marginVertical: 1 * kStyleConst.kSpacing,
  },
  my2: {
    marginVertical: 2 * kStyleConst.kSpacing,
  },
  my4: {
    marginVertical: 4 * kStyleConst.kSpacing,
  },
  orderSummaryAlertBodyText: {
    ...kFonts.poppinsLight,
    lineHeight: 5 * kStyleConst.kSpacing,
    marginTop: 4 * kStyleConst.kSpacing,
    textAlign: "center",
  },
  orderSummaryAlertContainer: {
    borderColor: kColors.brandDarkGreen,
    borderRadius: 2,
    borderWidth: 2,
    justifyContent: "center",
    marginVertical: 4 * kStyleConst.kSpacing,
    paddingHorizontal: 6 * kStyleConst.kSpacing,
    paddingVertical: 4 * kStyleConst.kSpacing,
    width: "100%",
  },
  orderSummaryAlertHeaderText: {
    ...kFonts.poppinsRegularMedium,
    alignSelf: "center",
    color: kColors.black,
  },
  orderSummaryContainer: {
    alignContent: "center",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  orderSummaryText: {
    alignSelf: "center",
    fontFamily: "Poppins",
    fontSize: 11,
    fontWeight: "400",
    letterSpacing: 1.1,
  },
  orderSummaryTotalCheckout: {
    ...kFonts.poppinsLight,
    alignSelf: "center",
    color: kColors.white,
    fontSize: kStyleConst.kFontSizeMedium,
    textTransform: "uppercase",
  },
  sizeSm: {
    height: 12,
    width: 12,
  },
  textUpperCase: {
    textTransform: "uppercase",
  },
  topBannerContainer: {
    backgroundColor: kColors.brandDarkGreen,
    justifyContent: "center",
    paddingHorizontal: 4 * kStyleConst.kSpacing,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  totalAmount: {
    alignSelf: "center",
    color: kColors.brandDark,
    fontFamily: "Poppins",
    fontSize: kStyleConst.kFontSizeMedium,
    fontWeight: "400",
    letterSpacing: 1.1,
  },
  totalLabel: {
    alignSelf: "center",
    color: kColors.brandDark,
    fontFamily: "Raleway",
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 1.1,
    textTransform: "uppercase",
  },
  widthFull: {
    width: "100%",
  },
  widthMd: {
    width: 350,
  },
});
